/**
 * Custom hook for managing content drag and drop functionality
 */
import { useState, useCallback } from 'react';
import { Content } from '../useContentData';
import { useTimezone } from '@/hooks/useTimezone';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/use-toast';

export interface UseContentDragDropProps {
  onRefresh: () => Promise<void>;
  currentMonth: Date;
}

export interface UseContentDragDropReturn {
  // Drag state
  draggedItem: Content | null;
  setDraggedItem: (item: Content | null) => void;
  dragOverDay: number | null;
  setDragOverDay: (day: number | null) => void;
  
  // Drag handlers
  isContentDraggable: (content: Content) => boolean;
  handleDragStart: (e: React.DragEvent, content: Content) => void;
  handleDragEnd: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent, day: number) => void;
  handleDragLeave: () => void;
  handleDrop: (e: React.DragEvent, day: number) => Promise<void>;
}

/**
 * Hook for managing content drag and drop functionality
 */
export const useContentDragDrop = ({ 
  onRefresh, 
  currentMonth 
}: UseContentDragDropProps): UseContentDragDropReturn => {
  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState<Content | null>(null);
  const [dragOverDay, setDragOverDay] = useState<number | null>(null);
  
  // Get timezone utilities
  const { 
    timezone,
    formatDate: formatDateInUserTimezone,
  } = useTimezone();

  /**
   * Check if content is draggable (only HOLD or SCHEDULED posts)
   */
  const isContentDraggable = useCallback((content: Content): boolean => {
    return content.display_status === 'HOLD' || 
           content.display_status === 'HELD' || 
           content.display_status === 'SCHEDULED' || 
           content.is_scheduled === true;
  }, []);

  /**
   * Handle drag start event
   */
  const handleDragStart = useCallback((e: React.DragEvent, content: Content) => {
    if (!isContentDraggable(content)) {
      e.preventDefault();
      return;
    }
    
    setDraggedItem(content);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', content.id);
    
    // Set drag image with some transparency
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '0.5';
  }, [isContentDraggable]);

  /**
   * Handle drag end event
   */
  const handleDragEnd = useCallback((e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '1';
    setDraggedItem(null);
    setDragOverDay(null);
  }, []);

  /**
   * Handle drag over day cell
   */
  const handleDragOver = useCallback((e: React.DragEvent, day: number) => {
    if (!draggedItem) return;
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(day);
  }, [draggedItem]);

  /**
   * Handle drag leave day cell
   */
  const handleDragLeave = useCallback(() => {
    setDragOverDay(null);
  }, []);

  /**
   * Handle drop on day cell
   */
  const handleDrop = useCallback(async (e: React.DragEvent, day: number) => {
    e.preventDefault();
    setDragOverDay(null);
    
    if (!draggedItem || !timezone) {
      toast({
        title: "Error",
        description: "Unable to update schedule - missing timezone information",
        variant: "destructive"
      });
      return;
    }

    try {
      // Get the current scheduled time to preserve hour/minute
      const originalScheduledTime = draggedItem.scheduled_time;
      if (!originalScheduledTime) {
        toast({
          title: "Error",
          description: "Cannot reschedule content without existing scheduled time",
          variant: "destructive"
        });
        return;
      }

      // Parse original time to get hour and minute
      const originalDate = new Date(originalScheduledTime);
      const hours = originalDate.getHours();
      const minutes = originalDate.getMinutes();

      // Create new date with the target day but preserve original time
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const newDate = new Date(year, month, day, hours, minutes, 0, 0);

      // Convert to UTC for storage (since scheduled times are stored in UTC)
      const utcDate = new Date(newDate.toISOString());
      
      // Update the scheduled time in Supabase
      const { error } = await supabase
        .from('scheduled_social_posts')
        .update({ 
          scheduled_time: utcDate.toISOString()
        })
        .eq('content_id', draggedItem.id);

      if (error) {
        console.error('Error updating scheduled time:', error);
        toast({
          title: "Error",
          description: "Failed to update schedule",
          variant: "destructive"
        });
        return;
      }

      // Refresh the content data to show updated position
      await onRefresh();
      
      toast({
        title: "Schedule Updated",
        description: `Content moved to ${newDate.toLocaleDateString()} at ${formatDateInUserTimezone(newDate.toISOString(), 'h:mm a')}`
      });
      
    } catch (error) {
      console.error('Error in drag drop operation:', error);
      toast({
        title: "Error",
        description: "Failed to update schedule",
        variant: "destructive"
      });
    }

    setDraggedItem(null);
  }, [draggedItem, timezone, currentMonth, onRefresh, formatDateInUserTimezone]);

  return {
    draggedItem,
    setDraggedItem,
    dragOverDay,
    setDragOverDay,
    isContentDraggable,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
};